import type { CatalogItem } from '@viva/core';
import { LazyImage } from '@viva/ui';
import { imageUrl } from '../network';
import { isOpenable, isPlayable } from '../state/playback-context';
import { toggleFavorite, useFavorite } from '../channel-memory';
import { t } from '../i18n';
import { Icon } from './Icon';

export function ChannelRow({ item, index, onOpen }: { item: CatalogItem | undefined; index: number; onOpen: (item: CatalogItem) => void }) {
  const playable = isPlayable(item);
  return (
    <div className="channel-row__wrap">
      <button type="button" className="channel-row" aria-busy={!item} disabled={!playable} onClick={() => item && onOpen(item)}>
        <span className="channel-row__number muted">{item?.data.channelNumber ?? index + 1}</span>
        {item ? <LazyImage className="channel-row__logo" src={imageUrl(item.logo)} label={item.name} /> : <span className="channel-row__logo skeleton" />}
        <span className="channel-row__name">{item?.name ?? ''}</span>
      </button>
      {item && playable ? <FavoriteButton item={item} className="channel-row__star" /> : null}
    </div>
  );
}

/** ☆ / ★ for a live channel: adds it to or removes it from Favorites in Live TV. */
export function FavoriteButton({ item, className }: { item: CatalogItem; className: string }) {
  const favorite = useFavorite(item);
  return (
    <button
      type="button"
      className={`favorite-button ${className}`}
      aria-pressed={favorite}
      aria-label={favorite ? t('favorites.remove', { name: item.name }) : t('favorites.add', { name: item.name })}
      title={favorite ? t('favorites.remove', { name: item.name }) : t('favorites.add', { name: item.name })}
      onClick={(event) => {
        event.stopPropagation();
        toggleFavorite(item);
      }}
    >
      <Icon name="star" filled={favorite} />
    </button>
  );
}

interface PosterCardProps {
  item: CatalogItem | undefined;
  onOpen: (item: CatalogItem) => void;
  /** Shown before the title, e.g. "S1 E3" in Continue watching. */
  caption?: string;
  /** Share watched, 0 to 1: a bar along the bottom of the poster. */
  progress?: number;
}

export function PosterCard({ item, onOpen, caption, progress }: PosterCardProps) {
  return (
    <button type="button" className="poster" aria-busy={!item} disabled={!isOpenable(item)} onClick={() => item && onOpen(item)}>
      <span className="poster__frame">
        {item ? <LazyImage className="poster__image" src={imageUrl(item.logo)} label={item.name} /> : <span className="poster__image skeleton" />}
        {progress !== undefined ? (
          <span className="poster__progress">
            <span className="poster__progress-bar" style={{ width: `${Math.round(Math.min(1, Math.max(0, progress)) * 100)}%` }} />
          </span>
        ) : null}
      </span>
      <span className="poster__title">{caption ? `${caption} · ${item?.name ?? ''}` : (item?.name ?? '')}</span>
    </button>
  );
}
